// frontend/src/components/UserRoleIndicator.jsx
// User Role Indicator Component
// Purpose: Displays user's role and permissions in the UI

import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";

const UserRoleIndicator = ({
  showDetails = false,
  className = "",
  compact = false,
}) => {
  const {
    user,
    isAuthenticated,
    getRoleDisplayName: getRoleDisplayNameHelper,
  } = useAuth();
  const { t, i18n } = useTranslation();
  const [showPermissions, setShowPermissions] = useState(false);
  const isRTL = i18n.language === 'he';

  if (!isAuthenticated || !user) {
    return null;
  }

  const roleDisplayNameEnglish = getRoleDisplayNameHelper();
  // Translate role name
  const roleDisplayName = roleDisplayNameEnglish === "User" ? t("role_user") :
                          roleDisplayNameEnglish === "Moderator" ? t("role_moderator") :
                          roleDisplayNameEnglish === "Administrator" ? t("role_administrator") :
                          roleDisplayNameEnglish === "Super Administrator" ? t("role_super_administrator") :
                          roleDisplayNameEnglish === "Unknown" ? t("role_unknown") :
                          roleDisplayNameEnglish;

  // Get role color based on role level
  const getRoleColor = (role) => {
    const colors = {
      user: "bg-blue-100 text-blue-800",
      moderator: "bg-green-100 text-green-800",
      admin: "bg-purple-100 text-purple-800",
      super_admin: "bg-red-100 text-red-800",
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  // Parse permissions for display
  // Handle both string (comma-separated) and array formats
  const parsePermissions = (permissions) => {
    if (!permissions) return [];
    
    // If it's already an array, return it (filtered and trimmed)
    if (Array.isArray(permissions)) {
      return permissions
        .map((p) => (typeof p === 'string' ? p.trim() : String(p).trim()))
        .filter((p) => p);
    }
    
    // If it's a string, split by comma
    if (typeof permissions === 'string') {
      return permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p);
    }
    
    // Fallback: convert to string and try to parse
    return [];
  };

  const userPermissions = parsePermissions(user.permissions);

  // Compact mode for header usage
  if (compact) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {/* Role Badge */}
        <span
          className={`inline-flex items-center px-3 py-2 h-9 rounded-lg text-sm font-medium ${getRoleColor(
            user.role
          )}`}
        >
          {roleDisplayName}
        </span>

        {/* Super User Badge - only show if different from role */}
        {user.is_superuser && user.role !== "super_admin" && (
          <span className="inline-flex items-center px-3 py-2 h-9 rounded-lg text-sm font-medium bg-red-100 text-red-800">
            {t("super_user")}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col space-y-2 ${className} ${isRTL ? 'text-right' : 'text-left'}`}>
      {/* User Info */}
      <div className={`text-sm text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
        {/* Only show level for privileged users (moderator+ or level >= 2) */}
        {(user.role_level >= 2 || user.role === 'moderator' || user.role === 'admin' || user.role === 'super_admin') && (
          <div className={isRTL ? 'text-right' : 'text-left'}>{t("level")}: {user.role_level}</div>
        )}
        <div className={isRTL ? 'text-right' : 'text-left'}>{t("email_label")}: {user.email}</div>
        {user.name && <div className={isRTL ? 'text-right' : 'text-left'}>{t("name_label")}: {user.name}</div>}
      </div>

      {/* Permissions Toggle */}
      {showDetails && userPermissions.length > 0 && (
        <div className={`mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
          <button
            onClick={() => setShowPermissions(!showPermissions)}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            {showPermissions ? t("hide_permissions") : t("show_permissions")} (
            {userPermissions.length})
          </button>

          {showPermissions && (
            <div className={`mt-2 p-3 bg-gray-50 rounded-md ${isRTL ? 'text-right' : 'text-left'}`}>
              <h4 className={`text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t("permissions")}:
              </h4>
              <div className="grid grid-cols-2 gap-1">
                {userPermissions.map((permission, index) => (
                  <div
                    key={index}
                    className={`text-xs text-gray-600 bg-white px-2 py-1 rounded ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    {permission}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserRoleIndicator;
